import { createContext, useContext, useRef } from "react"
import { firestoreDB, usingEmulator } from "../firebase"
import { useAuth } from "./AuthContext"
import { doc, setDoc, serverTimestamp, collection, getDoc, updateDoc, where, getDocs, deleteDoc } from "firebase/firestore";
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { storage, auth } from "../firebase";
import supabase from "../supabase";
import { getPrevSessionThreadId } from "../visualizer/firebase";

const DatabaseContext = createContext();

export function useDatabase() {
  return useContext(DatabaseContext);
}

export function DatabaseProvider({ children }) {

    const { currentUser } = useAuth();
    const [clientData, clientLoading, clientBad] = useDocumentData(doc(firestoreDB, "Clients", currentUser.uid));
    const workspaceConfigs = useRef({});

    async function createTeamsChannelConfig(docRef, workspaceId = null) {
        return setDoc(docRef, {
            createdAt: serverTimestamp(),
            initialized: false,
            uid: currentUser.uid,
            email: currentUser.email,
            workspaceId: workspaceId,
        });
    }

    function deleteTeamsChannelConfig(config) {
        return deleteDoc(doc(firestoreDB, "ChannelConfigs-Teams", config));
    }

    function generateTeamsChannelConfig() {
        return doc(collection(firestoreDB, "ChannelConfigs-Teams"));
    }

    function setWorkspaceConfigDoc(config, data) {
        const docRef = doc(firestoreDB, "WorkspaceConfigs", config);
        return setDoc(docRef, data);
    }

    async function getWorkspaceConfigDoc(config) {
        if (workspaceConfigs.current[config]) {
            console.log("Returning cached workspace config for " + config);
            return workspaceConfigs.current[config];
        }
        console.log("Fetching workspace config for " + config);
        const docData = (await getDoc(doc(firestoreDB, "WorkspaceConfigs", config))).data();
        workspaceConfigs.current[config] = docData;
        return docData;
    }

    function updateWorkspaceConfigDoc(config, data, updateCacheOnly = false) {
        console.log("Updating workspace config for " + config);
        if (updateCacheOnly) {
            workspaceConfigs.current[config] = { ...workspaceConfigs.current[config], ...data };
        } else {
            workspaceConfigs.current[config] = { ...workspaceConfigs.current[config], ...data };
            return updateDoc(doc(firestoreDB, "WorkspaceConfigs", config), data);
        }
    }

    function useTeamsChannelConfigDoc(config) {
        return useDocumentData(doc(firestoreDB, "ChannelConfigs-Teams", config));
    }

    function useWorkspaceConfigDoc(config) {
        return useDocumentData(doc(firestoreDB, "WorkspaceConfigs", config));
    }

    async function uploadFile(file) {
        console.log("Uploading file: " + file.path);
        const storageRef = ref(storage, `ClientIcons/${currentUser.uid}/${file.name}`);
        //const fileRef = storageRef.child(file.name);
        //await fileRef.put(file.content, { contentType: file.type });
        //return fileRef;
        console.log("Uploading file: " + file.name);
        console.log("File type: " + file.type);
        const metadata = {
            contentType: file.type,
            fileName: file.name,
        };
    
        const uploadTask = uploadBytesResumable(storageRef, file, metadata);
        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log('Upload is ' + progress + '% done');
                    switch (snapshot.state) {
                        case 'paused':
                            console.log('Upload is paused');
                            break;
                        case 'running':
                            console.log('Upload is running');
                            break;
                        default:
                            console.log('Upload is in an unknown state');
                    }
                },
                (error) => {
                    console.error(error.code, error.message);
                    reject(error);
                },
                () => {
                    //resolve(uploadTask.snapshot.ref);
                    getDownloadURL(uploadTask.snapshot.ref)
                        .then((downloadURL) => {
                            console.log('File available at', downloadURL);
                            resolve(downloadURL);
                        })
                        .catch((error) => {
                            reject(error);
                        });
                }
            );
        });
    }

    async function deleteFile(fileUrl) {
        const storageRef = ref(storage, fileUrl);
        return deleteObject(storageRef);
    }

    // Begin Supabase Analytics Events

    const initialized = useRef(false);

    async function createInitializationEvent() {
        if (usingEmulator) {
            console.log("Using emulator, skipping initialization event");
            return;
        }
        if (initialized.current) {
            console.log("Initialization event already created");
            return;
        }
        initialized.current = true;
        const { data, error } = await supabase.from("initializations").insert({
            type: "web",
            active_threads: getPrevSessionThreadId() ? 1 : 0,
            active_configs: Object.keys(workspaceConfigs.current).length + 1,
            uid: currentUser.uid,
        });
        if (error) {
            console.error(error);
        }
        console.log("Initialization event created:", data);
    }

    async function createErrorEvent(error_code, error_message, action, diabolical) {
        if (usingEmulator) {
            console.log("Using emulator, skipping error event");
            return;
        }
        const { data, error } = await supabase.from("errors").insert({
            uid: currentUser.uid,
            error_code,
            error_message,
            diabolical,
            action,
        });
        if (error) {
            console.error(error);
        }
        console.log("Error event created:", data);
    }

    async function createWorkspaceActionEvent(action, workspace_type, workspace) {
        if (usingEmulator) {
            console.log("Using emulator, skipping workspace action event");
            return;
        }
        const { data, error } = await supabase.from("workspace_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            action,
        });
        if (error) {
            console.error(error);
        }
        console.log("Workspace action event created:", data);
    }
    
    async function createSubscriptionActionEvent(action, amount, workspace_type = null, workspace = null, addons = null) {
        if (usingEmulator) {
            console.log("Using emulator, skipping subscription action event");
            return;
        }
        const { data, error } = await supabase.from("subscription_actions").insert({
            uid: currentUser.uid,
            action,
            amount,
            workspace_type,
            workspace,
            addons,
        });
        if (error) {
            console.error(error);
        }
        console.log("Subscription action event created:", data);
    }

    async function createAccountActionEvent(action) {
        if (usingEmulator) {
            console.log("Using emulator, skipping account action event");
            return;
        }
        const { data, error } = await supabase.from("account_actions").insert({
            uid: currentUser.uid,
            action,
        });
        if (error) {
            console.error(error);
        }
        console.log("Account action event created:", data);
    }

    async function createDesignActionEvent(workspace_type, workspace, theme, main_icon, popup_icon, title_icon, upload) {
        if (usingEmulator) {
            console.log("Using emulator, skipping design action event");
            return;
        }
        console.log("Creating design action event for workspace_type:", workspace_type, "workspace:", workspace, "theme:", theme, "main_icon:", main_icon, "popup_icon:", popup_icon, "title_icon:", title_icon, "upload:", upload);
        const { data, error } = await supabase.from("design_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            theme,
            main_icon,
            popup_icon,
            title_icon,
            upload,
        });
        if (error) {
            console.error(error);
        }
        console.log("Design action event created:", data);
    }

    async function createVisualizerActionEvent(workspace_type, workspace, is_enabled) {
        if (usingEmulator) {
            console.log("Using emulator, skipping visualizer action event");
            return;
        }
        const { data, error } = await supabase.from("visualizer_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            is_enabled,
        });
        if (error) {
            console.error(error);
        }
        console.log("Visualizer action event created:", data);
    }

    async function createBaselineActionEvent(workspace_type, workspace, chat_title, new_msg_title, welcome_msg, contact, activity, outside_hours, offline_msg, days_open) {
        if (usingEmulator) {
            console.log("Using emulator, skipping baseline action event");
            return;
        }
        const { data, error } = await supabase.from("baseline_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            chat_title,
            new_msg_title,
            welcome_msg,
            contact,
            activity,
            outside_hours,
            offline_msg,
            days_open,
        });
        if (error) {
            console.error(error);
        }
        console.log("Baseline action event created:", data);
    }

    async function createFileUploadActionEvent(workspace_type, workspace, allowed_types) {
        if (usingEmulator) {
            console.log("Using emulator, skipping file upload action event");
            return;
        }
        const { data, error } = await supabase.from("file_upload_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            allowed_types,
        });
        if (error) {
            console.error(error);
        }
        console.log("File upload action event created:", data);
    }

    async function createAutoEngageActionEvent(workspace_type, workspace, timer, num_engage) {
        if (usingEmulator) {
            console.log("Using emulator, skipping auto engage action event");
            return;
        }
        const { data, error } = await supabase.from("auto_engage_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            timer,
            num_engage,
        });
        if (error) {
            console.error(error);
        }
        console.log("Auto engage action event created:", data);
    }
    
    async function createAutoReplyActionEvent(workspace_type, workspace, num_reply) {
        if (usingEmulator) {
            console.log("Using emulator, skipping auto reply action event");
            return;
        }
        const { data, error } = await supabase.from("auto_reply_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            num_reply,
        });
        if (error) {
            console.error(error);
        }
        console.log("Auto reply action event created:", data);
    }

    async function createContactModuleActionEvent(workspace_type, workspace, title, inbox, email, phone, address, ygm, recaptcha, site_key, secret_key) {
        if (usingEmulator) {
            console.log("Using emulator, skipping contact module action event");
            return;
        }
        const { data, error } = await supabase.from("contact_module_actions").insert({
            uid: currentUser.uid,
            workspace_type,
            workspace,
            title,
            inbox,
            email,
            phone,
            address,
            ygm,
            recaptcha,
            site_key,
            secret_key,
        });
        if (error) {
            console.error(error);
        }
        console.log("Contact module action event created:", data);
    }
    

    // End Supabase Analytics Events

    const contextValues = {
        clientData,
        clientLoading,
        clientBad,
        createTeamsChannelConfig,
        generateTeamsChannelConfig,
        useTeamsChannelConfigDoc,
        setWorkspaceConfigDoc,
        useWorkspaceConfigDoc,
        getWorkspaceConfigDoc,
        updateWorkspaceConfigDoc,
        uploadFile,
        deleteFile,
        deleteTeamsChannelConfig,

        createInitializationEvent,
        createErrorEvent,
        createWorkspaceActionEvent, // delete, add_channel, delete_channel
        createSubscriptionActionEvent, // subscribe, resubscribe, cancel, delete, inc_addons, dec_addons
        createAccountActionEvent,
        createDesignActionEvent,
        createVisualizerActionEvent,
        createBaselineActionEvent,
        createFileUploadActionEvent,
        createAutoEngageActionEvent,
        createAutoReplyActionEvent,
        createContactModuleActionEvent,
    }

    return (
        <DatabaseContext.Provider value={contextValues}>
            {children}
        </DatabaseContext.Provider>
    )
}